import EditManufacturingForm from '@components/forms/EditManufacturingForm';
import EditSkuForm from '@components/forms/EditSkuForm';
import { EcoButton, EcoCard, EcoTab, EcoTabContent, EcoTabs, EcoText } from '@components/shared';
import EcoScrollView from '@components/views/EcoScrollView';
import { MerchantAdmin } from '@ecocart/entities';
import { useGlobal } from '@hooks/useGlobal';
import { useOverlay } from '@hooks/useOverlay';
import { BackLink } from '@navigation/BackLink';
import { GlobalScreenProps } from '@navigation/types';
import { useQuery } from '@tanstack/react-query';
import { getMerchant, getMerchantAdmin } from '@utils/api/merchant';
import {
  getCurrentProductName,
  getShopifyScopes,
  getShopifyScripts,
  regenerateShopifyVariants,
  triggerProductNameUpdate
} from '@utils/api/shopify';
import { sendMerchantAdminUpdateMessage } from '@utils/api/slack';
import { EcoCartMerchantTabs } from '@utils/constants/tabs';
import { Gap } from '@utils/layout';
import { FontColor } from '@utils/tailwind';
import React, { useEffect, useState } from 'react';
import { Clipboard, Pressable, View } from 'react-native';

const FULFILLMENT_SCOPES = [
  'read_merchant_managed_fulfillment_orders',
  // 'write_merchant_managed_fulfillment_orders', ** DROPPED May 25, 2023
  'read_assigned_fulfillment_orders',
  // 'write_assigned_fulfillment_orders', ** DROPPED May 25, 2023
  'read_third_party_fulfillment_orders',
  // 'write_third_party_fulfillment_orders', ** DROPPED May 25, 2023
  'read_fulfillments',
  'write_fulfillments'
];

export default function MerchantSetupScreen({ route }: GlobalScreenProps): JSX.Element {
  const shopName = route?.params?.shopName;
  const { data: merchant } = useQuery(['merchant', shopName], () => getMerchant(shopName));
  const { data: merchantAdmin, refetch } = useQuery(['merchantAdmin', shopName], () => getMerchantAdmin(shopName));
  const { data: scopes } = useQuery(['scopes', shopName], () => getShopifyScopes(shopName));
  const { data: scripts, isLoading: scriptsAreLoading } = useQuery(['scripts', shopName], () => getShopifyScripts(shopName));
  const { session } = useGlobal();
  const { showWarning, handleError, showSuccess } = useOverlay();
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
  const [productName, setProductName] = useState('');

  const INSTALL_SCRIPT = `<!-- EcoCart Carbon Offset -->
  	<script async src="https://widget.ecocart.tools/ecocart.js?shop_name=${merchant?.shopName}"></script>
  <!-- End EcoCart -->`;

  useEffect(() => {
    if (!merchantAdmin || productName) return;

    evalCurrentProductName(merchantAdmin);
  }, [merchantAdmin]);

  const evalCurrentProductName = ({ shopName, accessToken }: MerchantAdmin) => {
    getCurrentProductName(shopName, accessToken).then((res: any) => {
      setProductName(res?.products?.[0].title || '(error fetching product name)');
    });
  };
  //   const fetchCheckoutLiquidAsset = async () => {
  //     if (!merchant?.shopName) return;

  //     setIsFetchingCheckoutLiquid(true);

  //     getShopCheckoutLiquidAsset(merchant.shopName)
  //       .then((res) => {
  //         console.log(res);
  //         // setEcoRefs(res);
  //         setCheckoutLiquid(res?.value);
  //         setIsFetchingCheckoutLiquid(false);
  //       })
  //       .catch((error) => {
  //         handleError(error);
  //         setIsFetchingCheckoutLiquid(false);
  //       });
  //   };

  // const reInitializeMerchant = () => {
  //   if (!merchant?.shopName) return;

  //   initializeMerchant(merchant.shopName).then((res) => {
  //     console.log(res);
  //     // setEcoRefs(res);
  //     // setCheckoutLiquid(res?.value);
  //   });
  // };

  // const handleUpdateCheckoutLiquid = (html: string) => {
  //   if (!merchant?.shopName) return;

  //   updateShopCheckoutLiquidAsset(merchant.shopName, html).then((res) => {
  //     console.log(res);
  //     // setEcoRefs(res);
  //     // setCheckoutLiquid(res?.value);
  //   });
  // };

  const handleRegenerateVariantsPress = async () => {
    const isConfirmed = confirm(
      `Are you sure you want to regenerate ${shopName}'s variants? This process will effect their shopify store and may take a few minutes to complete!`
    );
    if (!isConfirmed || !shopName || !merchantAdmin?.accessToken) return;

    try {
      const dbMerchantAdmin = await getMerchantAdmin(shopName);

      if (dbMerchantAdmin.setupStep === 'variant_creation') {
        showWarning(`System is currently creating ${shopName}'s variants. Please try again shortly.`);
      } else {
        setIsRegenerating(true);
        await regenerateShopifyVariants(shopName, merchantAdmin.accessToken);
        setIsRegenerating(false);
        await sendMerchantAdminUpdateMessage('shopify_variants_regenerated', session, { merchantAdmin });
        showSuccess('Successfully regenerated variants');
      }
    } catch (error) {
      handleError(error as any);
      setIsRegenerating(false);
    }
  };

  const handleUpdateProductTitle = async (title: string) => {
    const isConfirmed = confirm(
      `Are you sure you want to update ${shopName}'s product title to ${title}? This process will effect their shopify store!`
    );
    if (!isConfirmed || !shopName || !merchantAdmin?.accessToken || !merchant?.offsetMultiplier) return;

    try {
      await triggerProductNameUpdate(shopName, merchant.offsetMultiplier, merchantAdmin.accessToken);
      await sendMerchantAdminUpdateMessage('shopify_product_title_updated', session, { merchantAdmin });

      evalCurrentProductName(merchantAdmin);
      showSuccess('Successfully updated product title');
    } catch (error) {
      handleError(error as any);
    }
  };

  const getInStoreScriptData = (_scripts: any): { message: string; fontColor: FontColor } => {
    if (scriptsAreLoading) {
      return { message: 'Loading...', fontColor: 'subdued' };
    } else {
      const hasScript = (_scripts?.script_tags?.[0]?.src || '').includes('ecocart.js');
      return { message: hasScript ? 'Enabled' : 'Disabled', fontColor: hasScript ? 'accent-primary' : 'danger' };
    }
  };

  const handleClipboardPress = () => {
    Clipboard.setString(INSTALL_SCRIPT);
    showWarning('Script copied to clipboard!');
  };

  return (
    <EcoScrollView>
      <View className="flex-row">
        <BackLink href="/merchants" label="Back to Merchants" />
      </View>
      <EcoTabs
        tabs={EcoCartMerchantTabs.map(({ label, screenName }) => {
          return {
            label,
            screenName,
            screenParams: { shopName }
          } as EcoTab;
        })}
        prefix="Merchant"
        activeScreen="MerchantSetup"
      >
        <EcoTabContent>
          <View style={Gap()}>
            {/* {merchant?.shopName ? (
          <View style={Gap()}>
          <EcoCard
          title={merchant.shopName}
          topRightComponent={
            <EcoButton size="sm" onPress={reInitializeMerchant}>
            Re-Initialize
            </EcoButton>
          }
          >
          <></>
          </EcoCard>
          
          <ShopifyDiagnosticsCard merchant={merchant} />
          </View>
        ) : null} */}
            <EcoCard title="Manufacturing">
              {merchantAdmin && <EditManufacturingForm merchantAdmin={merchantAdmin} onSuccess={refetch} />}
            </EcoCard>
            <EcoCard title="Shopify SKU">{merchantAdmin && <EditSkuForm merchantAdmin={merchantAdmin} />}</EcoCard>
            <EcoCard
              title={`Shopify Variants ${merchant?.variantIds?.[0] ? '(available)' : '(unavailable)'}`}
              topRightComponent={
                <EcoButton isDisabled={isRegenerating} size="sm" onPress={handleRegenerateVariantsPress}>
                  Regenerate Variants
                </EcoButton>
              }
            >
              <EcoText fontSize="base" color="subdued">
                {(merchant?.variantIds || []).join(', ')}
              </EcoText>
            </EcoCard>
            <EcoCard
              title={`Shopify Scopes ${
                FULFILLMENT_SCOPES.every((scope) => (scopes || []).includes(scope)) ? '(available)' : '(unavailable)'
              }`}
            >
              <EcoText fontSize="base" color="subdued">
                {(scopes || []).join(', ')}
              </EcoText>
            </EcoCard>
            <EcoCard title="Shopify Scripts">
              <View className="flex-row flex-start">
                <EcoText className="w-[80px]" color="subdued">
                  In-Store:
                </EcoText>
                <EcoText fontWeight="semibold" color={getInStoreScriptData(scripts).fontColor}>
                  {getInStoreScriptData(scripts).message}
                </EcoText>
              </View>
              <View className="flex-row flex-start">
                <EcoText className="w-[80px]" color="subdued">
                  Checkout:
                </EcoText>
                <EcoText fontWeight="semibold" color="subdued">
                  Unknown
                </EcoText>
              </View>
            </EcoCard>
            {merchantAdmin?.offsetMultiplier ? (
              <EcoCard
                title="Shopify Product Title"
                topRightComponent={
                  <EcoButton
                    size="sm"
                    onPress={() =>
                      handleUpdateProductTitle(merchantAdmin?.offsetMultiplier > 1 ? '"Climate Positive Order"' : '"Carbon Neutral Order"')
                    }
                  >
                    Set to {merchantAdmin?.offsetMultiplier > 1 ? '"Climate Positive Order"' : '"Carbon Neutral Order"'}
                  </EcoButton>
                }
              >
                <EcoText>{productName}</EcoText>
              </EcoCard>
            ) : null}
            {/* <EcoCard
              title="Shopify Checkout"
              topRightComponent={
                <EcoButton size="sm" onPress={fetchCheckoutLiquidAsset}>
                  {isFetchingCheckoutLiquid ? <EcoLoader size="small" /> : 'Fetch checkout.liquid'}
                </EcoButton>
              }
            >
              <View style={Gap()}> */}
            <Pressable onPress={handleClipboardPress}>
              <EcoCard title="Click Script to Copy">
                <EcoText fontWeight="semibold" fontSize="sm">
                  {INSTALL_SCRIPT}
                </EcoText>
              </EcoCard>
            </Pressable>
            {/* {checkoutLiquid ? (
                  <EcoCard title="layout/checkout.liquid">
                    <EditHTMLForm html={checkoutLiquid} onSuccess={handleUpdateCheckoutLiquid} />
                  </EcoCard>
                ) : null}
              </View>
            </EcoCard> */}
          </View>
        </EcoTabContent>
      </EcoTabs>
    </EcoScrollView>
  );
}
